import React, { useState, useEffect, useRef } from "react";

const OrmieJumpingGame = () => {
  const [currentPosition, setCurrentPosition] = useState(0);
  const [pathHistory, setPathHistory] = useState([0]);
  const [completedPaths, setCompletedPaths] = useState([]);
  const [isJumping, setIsJumping] = useState(false);
  const [showArrow, setShowArrow] = useState(false);
  const [jumpFrom, setJumpFrom] = useState(0);
  const [jumpTo, setJumpTo] = useState(0);
  const [showTrajectory, setShowTrajectory] = useState(false);
  const [trajectoryHistory, setTrajectoryHistory] = useState([]);
  const [stepPositions, setStepPositions] = useState({});
  const [svgDimensions, setSvgDimensions] = useState({
    width: 800,
    height: 200,
  });

  // 为每个台阶创建ref
  const stepRefs = useRef({});
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const gameAreaRef = useRef(null);

  const steps = [0, 1, 2, 3, 4, 5, 6];

  // 检测是否为移动设备或窄屏幕
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 计算台阶的实际位置和SVG尺寸
  const calculateStepPositions = () => {
    const positions = {};
    const gameAreaRect = gameAreaRef.current?.getBoundingClientRect();

    if (!gameAreaRect) return;

    // 动态计算SVG尺寸
    const svgWidth = gameAreaRect.width;
    const svgHeight = Math.max(200, isMobile ? 150 : 200);
    setSvgDimensions({ width: svgWidth, height: svgHeight });

    steps.forEach((step) => {
      const stepElement = stepRefs.current[step];
      if (stepElement) {
        const stepRect = stepElement.getBoundingClientRect();
        // 计算相对于游戏区域的位置
        const centerX = stepRect.left + stepRect.width / 2 - gameAreaRect.left;
        const centerY = stepRect.top + stepRect.height / 2 - gameAreaRect.top;
        positions[step] = { x: centerX, y: centerY };
      }
    });

    setStepPositions(positions);
    console.log("计算的台阶位置:", positions);
    console.log("SVG尺寸:", { width: svgWidth, height: svgHeight });
  };

  // 在组件挂载和布局变化时重新计算位置
  useEffect(() => {
    const handleResize = () => {
      setTimeout(calculateStepPositions, 100);
    };

    // 多次尝试计算，确保DOM完全渲染
    const timeouts = [100, 300, 500, 1000].map((delay) =>
      setTimeout(calculateStepPositions, delay)
    );

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      timeouts.forEach(clearTimeout);
    };
  }, [isMobile]);

  // 当台阶布局可能改变时重新计算
  useEffect(() => {
    setTimeout(calculateStepPositions, 150);
  }, [currentPosition, isMobile]);

  const resetGame = () => {
    setCurrentPosition(0);
    setPathHistory([0]);
    setIsJumping(false);
    setShowArrow(false);
    setTrajectoryHistory([]);
    setTimeout(calculateStepPositions, 200);
  };

  const clearHistory = () => {
    setCompletedPaths([]);
  };

  const jump = async (stepSize) => {
    if (isJumping || currentPosition >= 6) return;

    const newPosition = currentPosition + stepSize;
    if (newPosition > 6) return;

    setIsJumping(true);
    setJumpFrom(currentPosition);
    setJumpTo(newPosition);
    setShowArrow(true);

    // 记录轨迹
    const newTrajectory = { from: currentPosition, to: newPosition };
    setTrajectoryHistory((prev) => {
      const updated = [...prev, newTrajectory];
      console.log("更新轨迹历史:", updated);
      return updated;
    });

    // 延迟更新位置，创建动画效果
    setTimeout(() => {
      setCurrentPosition(newPosition);
      const newPath = [...pathHistory, newPosition];
      setPathHistory(newPath);

      if (newPosition === 6) {
        setCompletedPaths((prev) => [...prev, [...newPath]]);
      }

      setShowArrow(false);
      setIsJumping(false);
    }, 500);
  };

  const getStepStyle = (step) => {
    // 根据屏幕尺寸调整台阶大小
    const stepSize = isMobile ? "45px" : "60px";
    const stepHeight = isMobile ? "32px" : "40px";
    const fontSize = isMobile ? "12px" : "16px";

    const baseStyle = {
      width: stepSize,
      height: stepHeight,
      border: "2px solid #333",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: fontSize,
      fontWeight: "bold",
      position: "relative",
      transition: "all 0.3s ease",
      flexShrink: 0, // 防止压缩
    };

    if (step === 0) {
      return {
        ...baseStyle,
        backgroundColor: "#e8f5e8",
        borderColor: "#4CAF50",
        borderRadius: "50%",
        width: isMobile ? "40px" : "50px",
        height: isMobile ? "40px" : "50px",
      };
    }

    if (step === 6) {
      return {
        ...baseStyle,
        backgroundColor: "#fff3cd",
        borderColor: "#ffc107",
        boxShadow: currentPosition === 6 ? "0 0 20px #ffc107" : "none",
      };
    }

    return {
      ...baseStyle,
      backgroundColor: currentPosition === step ? "#d4edda" : "#f8f9fa",
      borderColor: currentPosition === step ? "#28a745" : "#333",
      transform: currentPosition === step ? "scale(1.1)" : "scale(1)",
    };
  };

  const getArrowPath = (fromStep, toStep) => {
    const fromPos = stepPositions[fromStep];
    const toPos = stepPositions[toStep];

    if (!fromPos || !toPos) {
      console.log(
        `无法获取位置信息: from=${fromStep}, to=${toStep}`,
        stepPositions
      );
      return "";
    }

    const startX = fromPos.x;
    const endX = toPos.x;
    const startY = fromPos.y;
    const endY = toPos.y;

    // 根据屏幕尺寸调整弧形高度
    const arcHeight = isMobile ? 30 : 40;
    const controlX = (startX + endX) / 2;
    const controlY = Math.min(startY, endY) - arcHeight;

    const path = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
    console.log(`生成路径: ${fromStep} -> ${toStep}:`, path);
    return path;
  };

  const formatPath = (path) => {
    return path.join(" → ");
  };

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto bg-gradient-to-b from-blue-50 to-white min-h-screen">
      <div className="text-center mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 mb-2">
          🐷 小猪Ormie跳台阶
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          帮助小猪从起点跳到第6个台阶！每次可以跳1格或2格
        </p>
      </div>

      {/* 游戏区域 */}
      <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 mb-4 sm:mb-6">
        <div ref={containerRef} className="relative">
          {/* 游戏台阶区域 */}
          <div
            ref={gameAreaRef}
            className="relative mb-6 sm:mb-8"
            style={{ minHeight: isMobile ? "120px" : "150px" }}
          >
            {/* 台阶容器 - 响应式布局 */}
            <div
              className={`
              flex items-center justify-center gap-2 sm:gap-4 pt-12
              ${isMobile ? "overflow-x-auto pb-2" : ""} 
              relative
            `}
            >
              {/* SVG覆盖层用于显示轨迹 */}
              <svg
                ref={svgRef}
                className="absolute top-0 left-0 w-full pointer-events-none"
                style={{
                  height: `${svgDimensions.height}px`,
                  zIndex: 10,
                }}
                viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
                preserveAspectRatio="xMidYMid meet"
              >
                {/* 当前跳跃动画箭头 */}
                {showArrow &&
                  stepPositions[jumpFrom] &&
                  stepPositions[jumpTo] && (
                    <>
                      <path
                        d={getArrowPath(jumpFrom, jumpTo)}
                        stroke="#ff6b6b"
                        strokeWidth={isMobile ? "3" : "4"}
                        fill="none"
                        strokeDasharray="8,4"
                        className="animate-pulse"
                      />
                      <circle
                        cx={stepPositions[jumpTo].x}
                        cy={stepPositions[jumpTo].y}
                        r={isMobile ? "4" : "6"}
                        fill="#ff6b6b"
                        className="animate-pulse"
                      />
                    </>
                  )}

                {/* 持续显示的轨迹线 */}
                {showTrajectory &&
                  trajectoryHistory.length > 0 &&
                  trajectoryHistory.map((trajectory, index) => {
                    const path = getArrowPath(trajectory.from, trajectory.to);
                    if (!path) return null;

                    return (
                      <g key={`trajectory-${index}`}>
                        <path
                          d={path}
                          stroke="#10b981"
                          strokeWidth={isMobile ? "2" : "3"}
                          fill="none"
                          opacity="0.8"
                          strokeLinecap="round"
                        />
                        {stepPositions[trajectory.to] && (
                          <circle
                            cx={stepPositions[trajectory.to].x}
                            cy={stepPositions[trajectory.to].y}
                            r={isMobile ? "3" : "4"}
                            fill="#10b981"
                            opacity="0.8"
                          />
                        )}
                      </g>
                    );
                  })}
              </svg>

              {/* 台阶显示 */}
              {steps.map((step) => (
                <div
                  key={step}
                  className="flex flex-col items-center flex-shrink-0"
                >
                  <div
                    ref={(el) => (stepRefs.current[step] = el)}
                    style={getStepStyle(step)}
                  >
                    {step === 0
                      ? isMobile
                        ? "起"
                        : "起点"
                      : step === 6
                      ? "🍪"
                      : step}
                    {currentPosition === step && step !== 0 && (
                      <div
                        className={`absolute ${
                          isMobile ? "-top-6 text-lg" : "-top-8 text-2xl"
                        }`}
                      >
                        🐷
                      </div>
                    )}
                    {currentPosition === 0 && step === 0 && (
                      <div
                        className={`absolute ${
                          isMobile ? "-top-6 text-lg" : "-top-8 text-2xl"
                        }`}
                      >
                        🐷
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {step === 0 ? "0" : step}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 当前路径显示 */}
          <div className="text-center mb-4">
            <div className="bg-blue-50 rounded-lg p-2 sm:p-3 inline-block max-w-full">
              <span className="text-xs sm:text-sm text-gray-600">
                当前路径:{" "}
              </span>
              <span className="font-mono text-blue-700 font-bold text-sm sm:text-base break-all">
                {formatPath(pathHistory)}
              </span>
            </div>
          </div>

          {/* 轨迹显示控制 */}
          <div className="flex flex-col items-center mb-4 space-y-2">
            <label className="flex items-center space-x-2 bg-gray-50 px-3 sm:px-4 py-2 rounded-lg border">
              <input
                type="checkbox"
                checked={showTrajectory}
                onChange={(e) => setShowTrajectory(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                显示跳跃轨迹 ✨
              </span>
            </label>

            {/* 调试信息 */}
            {!isMobile && (
              <div className="text-xs text-gray-500 text-center">
                <div>
                  轨迹记录数: {trajectoryHistory.length} | 台阶位置数:{" "}
                  {Object.keys(stepPositions).length}
                </div>
              </div>
            )}
          </div>

          {/* 控制按钮 */}
          <div className="flex justify-center gap-3 sm:gap-4 mb-4">
            <button
              onClick={() => jump(1)}
              disabled={
                isJumping || currentPosition >= 6 || currentPosition + 1 > 6
              }
              className={`
                bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed 
                text-white rounded-lg font-bold transition-colors
                ${isMobile ? "px-4 py-2 text-sm" : "px-6 py-3"}
              `}
            >
              跳1格 🦘
            </button>
            <button
              onClick={() => jump(2)}
              disabled={
                isJumping || currentPosition >= 6 || currentPosition + 2 > 6
              }
              className={`
                bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed 
                text-white rounded-lg font-bold transition-colors
                ${isMobile ? "px-4 py-2 text-sm" : "px-6 py-3"}
              `}
            >
              跳2格 🦘🦘
            </button>
          </div>

          {/* 游戏状态 */}
          <div className="text-center">
            {currentPosition === 6 && (
              <div className="bg-yellow-100 border border-yellow-400 rounded-lg p-3 sm:p-4 mb-4">
                <p className="text-yellow-800 font-bold text-base sm:text-lg">
                  🎉 恭喜！小猪成功吃到曲奇饼干！
                </p>
                <p className="text-yellow-700 text-sm sm:text-base break-all">
                  完整路径: {formatPath(pathHistory)}
                </p>
              </div>
            )}

            <button
              onClick={resetGame}
              className={`
                bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-bold transition-colors
                ${isMobile ? "px-4 py-2 text-sm" : "px-6 py-2"}
              `}
            >
              重新开始 🔄
            </button>
          </div>
        </div>
      </div>

      {/* 历史记录 */}
      <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">
            📝 完成的路径记录
          </h2>
          <button
            onClick={clearHistory}
            className={`
              bg-red-500 hover:bg-red-600 text-white rounded font-bold transition-colors
              ${isMobile ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm"}
            `}
          >
            清空记录
          </button>
        </div>

        {completedPaths.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm sm:text-base">
            还没有完成的路径，快来尝试吧！
          </p>
        ) : (
          <div className="space-y-2">
            {completedPaths.map((path, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-2 sm:p-3 border-l-4 border-blue-500"
              >
                <span className="text-xs sm:text-sm text-gray-600">
                  路径 {index + 1}:{" "}
                </span>
                <span className="font-mono text-gray-800 font-bold text-sm sm:text-base break-all">
                  {formatPath(path)}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 ml-2">
                  (共 {path.length - 1} 步)
                </span>
              </div>
            ))}
          </div>
        )}

        {completedPaths.length > 0 && (
          <div className="mt-4 p-3 sm:p-4 bg-blue-50 rounded-lg">
            <p className="text-blue-800 font-bold text-sm sm:text-base">
              📊 统计信息:
            </p>
            <p className="text-blue-700 text-sm sm:text-base">
              已找到 {completedPaths.length} 种不同的路径
            </p>
            <p className="text-blue-600 text-xs sm:text-sm mt-2">
              💡 提示: 从0到6总共有{" "}
              {(() => {
                const fib = [1, 1];
                for (let i = 2; i <= 6; i++) {
                  fib[i] = fib[i - 1] + fib[i - 2];
                }
                return fib[6];
              })()}{" "}
              种不同的跳法
            </p>
          </div>
        )}
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default OrmieJumpingGame;
